import { Injectable } from '@angular/core';
import { ImageEditorService } from '../../services/image-editor.service';
import { AccountService } from '../../services/account.service';
import { IImageFile } from '../../interfaces/files.interface';
import { Logger } from '../../monitoring/logger/logger';
import { FilesQuotaService } from '../quota/files-quota.service';
import { Observable } from 'rxjs';
import 'rxjs/add/observable/of';
import { CDFileManagerAPI } from './file-manager-api';
import { catchError, map, switchMap } from 'rxjs/operators';

interface IBlobData {
  cdnEndPoint: string;
  blobContainer: string;
}

interface IMongoFile {
  Id: string;
  Link: string;
  Name: string;
  CreatedOn: any;
  Width: number;
  Height: number;
  ContentType: string;
  Size: number;
  ParentFolderId: string;
}

@Injectable()
export class ImageFileEditorService {
  cdnEndPoint: string;
  blobContainer: string;
  languageId: string;
  imageFile: IImageFile;

  constructor(
    private imageEditorService: ImageEditorService,
    accountService: AccountService,
    private logger: Logger,
    private filesQuotaService: FilesQuotaService,
    private cdFileManagerAPI: CDFileManagerAPI
  ) {
    this.initBlobData();
    this.languageId = accountService.userlcid;
  }

  public editImage = (imageFile: IImageFile): Observable<IImageFile> => {
    if (this.filesQuotaService.isQuotaExceeded) {
      return this.filesQuotaService.openQuotaExceededDialog().onClose.pipe(map(() => {
        return null;
      }));
    }
    const publicImgUrl = this.getPublicImgUrl(imageFile.src);
    return this.imageEditorService.openImageEditor(publicImgUrl, this.languageId)
      .pipe(switchMap((fileDataFromImageEditor: any) => this.cdFileManagerAPI.saveFileFromPixie(imageFile, fileDataFromImageEditor)))
      .pipe(catchError(error => Observable.of(error)))
      .pipe(map((fileDataFromServer: IMongoFile) => {
        const imagefile: IImageFile = {
          id: fileDataFromServer.Id,
          src: fileDataFromServer.Link,
          name: fileDataFromServer.Name,
          uploadDate: this.formatDate(new Date(fileDataFromServer.CreatedOn)),
          width: fileDataFromServer.Width,
          height: fileDataFromServer.Height,
          contentType: fileDataFromServer.ContentType,
          size: fileDataFromServer.Size,
          parentFolderId: fileDataFromServer.ParentFolderId
        };
        this.filesQuotaService.subscribeToGetStorageQuota();
        return imagefile;
      }));

  }

  private getPublicImgUrl = (url: string): string => {
    //get url to image - to blob, without blob server handler - to pixie only (CORS issue (cross origin resurse sharing))
    let result = '';
    const questionMarkLastIndex = url.lastIndexOf('?');
    let fileName: string;
    if (questionMarkLastIndex > 0) {
      const fileSrcWithoutTimeStemp = url.substring(0, url.lastIndexOf('?'));
      fileName = fileSrcWithoutTimeStemp.substring(fileSrcWithoutTimeStemp.lastIndexOf('/') + 1);
    } else {
      fileName = url.substring(url.lastIndexOf('/') + 1);
    }

    result = `${this.cdnEndPoint}/${this.blobContainer}/files/${fileName}`;

    const windowLocationHref = window.location.href;
    const iswindowLocationHrefHttps = windowLocationHref.startsWith('https');
    const isImgPublicUrlsHttps = result.startsWith('https');
    if (iswindowLocationHrefHttps === isImgPublicUrlsHttps) {
      return result;
    }

    if (iswindowLocationHrefHttps === true) {
      result = result.replace('http', 'https');
      return result;
    }

    if (iswindowLocationHrefHttps === false) {
      result = result.replace('https', 'http');
      return result;
    }

    return result;
  }

  private initBlobData = () => {
    this.cdFileManagerAPI.getBlobData()
      .subscribe((blobData: IBlobData) => {
        if (blobData) {
          if (blobData.cdnEndPoint && blobData.blobContainer) {
            this.cdnEndPoint = blobData.cdnEndPoint;
            this.blobContainer = blobData.blobContainer;
          }
        }
      },
      (error) => this.logger.error(`error on loading blob container data: ${error.originalError || error}`)
    );
  }

  private formatDate = (d: any) => {
    const mS = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    const month = mS[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();
    return month + ' ' + day + ', ' + year;
  }
}
